/**
 * Template: Solicitação de Criação de Acesso Institucional
 *
 * Formulário para cadastrar colaboradores/terceiros que precisam de acessos institucionais.
 */

import type { TemplateSchema, TemplateSchemaOptions } from '../types'

/**
 * Calcula data mínima (120 anos atrás) e máxima (16 anos atrás) para data de nascimento
 */
function getDataNascimentoLimits() {
  const hoje = new Date()
  const dataMaxima = new Date(hoje.getFullYear() - 16, hoje.getMonth(), hoje.getDate())
  const dataMinima = new Date(hoje.getFullYear() - 120, hoje.getMonth(), hoje.getDate())
  
  return {
    min: dataMinima.toISOString().split('T')[0],
    max: dataMaxima.toISOString().split('T')[0],
  }
}

/**
 * Monta o schema do template RQ36.
 */
export function getSolicitacaoCriacaoAcessoInstitucionalSchema(
  _options?: TemplateSchemaOptions
): TemplateSchema {
  const dataNascimentoLimits = getDataNascimentoLimits()
  
  return {
    template_id: 'solicitacao_criacao_acesso_institucional',
    template_nome: 'Solicitação de Criação de Acesso Institucional',
    versao: '2.0.0',
    fluxoSimplificado: {
      ativo: true,
      demandanteAutomatico: true, // Automaticamente "Eu sou o demandante"
      tituloAutomatico: true, // Usa o nome do processo como título
      prioridadePadrao: 'Media', // Prioridade padrão
      diasEntregaPadrao: 3, // 3 dias para entrega
      ocultarInfoProcessos: true, // Oculta card de informação dos processos
      ocultarTitulo: true, // Oculta campo de título (já definido automaticamente)
      ocultarPrioridade: true, // Oculta campo de prioridade (já definido automaticamente)
      ocultarDataEntrega: true, // Oculta campo de data (já definido automaticamente)
      ocultarAnexos: true, // Oculta seção de anexos
      exibirHeaderTemplate: true, // Exibe header com título do template
    },
    schema: {
      type: 'object',
      title: 'Dados para criação de acesso institucional',
      description: 'Informe os dados pessoais e contratuais do colaborador que receberá o acesso.',
      required: [
        'nome',
        'cpf',
        'data_nascimento',
        'sexo',
        'telefone_particular',
        'email_particular',
        'tipo_contrato',
        'contrato_fornecedor',
        'contrato_objetivo',
        'inicio_contrato',
        'termino_contrato',
        'empresa_lotacao',
        'unidade',
        'cargo',
        //'email_institucional',
        //'afastado',
        //'microsoft_a5',
      ],
      properties: {
        secao_dados_pessoais: {
          type: 'null',
          title: 'Dados Pessoais',
          description: 'Informações básicas do colaborador.',
        },
        nome: {
          type: 'string',
          title: 'Nome Completo',
          minLength: 3,
        },
        cpf: {
          type: 'string',
          title: 'CPF',
          pattern: '^[0-9]{11}$',
          description: 'Informe apenas números (11 dígitos).',
        },
        data_nascimento: {
          type: 'string',
          title: 'Data de Nascimento',
          format: 'date',
          description: `Idade mínima: 16 anos. Idade máxima: 120 anos.`,
        },
        sexo: {
          type: 'string',
          title: 'Sexo',
          enum: ['', 'Masculino', 'Feminino', 'Outros'],
          default: '',
        },
        telefone_particular: {
          type: 'string',
          title: 'Telefone Particular',
          pattern: '^[0-9]{10,11}$',
          description: 'Informe apenas números com DDD (10 ou 11 dígitos).',
        },
        email_particular: {
          type: 'string',
          title: 'Email Particular',
          format: 'email',
          pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
        },

        secao_dados_contratuais: {
          type: 'null',
          title: 'Dados de Contrato',
        },
        tipo_contrato: {
          type: 'string',
          title: 'Tipo de Contrato',
          enum: ['', 'CLT', 'Temporário', 'Estagiário', 'Prestador de Serviço', 'Terceirizado'],
          default: '',
        },
        contrato_fornecedor: {
          type: 'string',
          title: 'Contrato Fornecedor',
          description: 'Selecione o fornecedor ou adicione um novo.',
        },
        contrato_objetivo: {
          type: 'string',
          title: 'Objetivo do Contrato',
          minLength: 10,
        },
        inicio_contrato: {
          type: 'string',
          title: 'Início do Contrato',
          format: 'date',
          description: 'Não pode ser anterior a 01/01/1970.',
        },
        termino_contrato: {
          type: 'string',
          title: 'Término do Contrato',
          format: 'date',
        },

        secao_dados_lotacao: {
          type: 'null',
          title: 'Dados de Lotação',
        },
        empresa_lotacao: {
          type: 'string',
          title: 'Empresa de Lotação',
          enum: ['', 'FIEAC', 'SESI', 'SENAI', 'IEL'],
          default: '',
        },
        unidade: {
          type: 'string',
          title: 'Unidade',
          description: 'Selecione a unidade de lotação.',
        },
        cargo: {
          type: 'string',
          title: 'Cargo',
          description: 'Selecione o cargo.',
        },
        // email_institucional: {
        //   type: 'string',
        //   title: 'Email Institucional',
        //   format: 'email',
        //   pattern: '^[a-zA-Z0-9._%+-]+@(fieac|sesi|senai|iel)\\.org\\.br$',
        //   description: 'Deve ser um email @fieac.org.br, @sesi.org.br, @senai.org.br ou @iel.org.br',
        // },
        // afastado: {
        //   type: 'boolean',
        //   title: 'Afastado',
        //   description: 'Indica se o colaborador está afastado.',
        //   default: false,
        // },
        // microsoft_a5: {
        //   type: 'boolean',
        //   title: 'Microsoft A5',
        //   description: 'Indica se o colaborador necessita de licença Microsoft A5.',
        //   default: false,
        // },
        // observacoes: {
        //   type: 'string',
        //   title: 'Observações',
        //   description: 'Informações adicionais sobre o colaborador ou o acesso.',
        // },
      },
    },
    uiSchema: {
      'ui:order': [
        'secao_dados_pessoais',
        'nome',
        'cpf',
        'data_nascimento',
        'sexo',
        'telefone_particular',
        'email_particular',
        'secao_dados_contratuais',
        'tipo_contrato',
        'contrato_fornecedor',
        'contrato_objetivo',
        'inicio_contrato',
        'termino_contrato',
        'secao_dados_lotacao',
        'empresa_lotacao',
        'unidade',
        'cargo',
        // 'email_institucional',
        // 'afastado',
        // 'microsoft_a5',
        // 'observacoes',
      ],
      nome: {
        'ui:placeholder': 'Digite o nome completo...',
        'ui:autofocus': true,
      },
      cpf: {
        'ui:placeholder': 'Somente números (ex: 12345678900)',
        'ui:widget': 'cpf',
      },
      data_nascimento: {
        'ui:widget': 'date',
        'ui:options': {
          min: dataNascimentoLimits.min,
          max: dataNascimentoLimits.max,
        },
      },
      sexo: {
        'ui:placeholder': 'Selecione o sexo',
        'ui:emptyValue': '',
      },
      telefone_particular: {
        'ui:placeholder': 'Ex: 6899999999',
      },
      email_particular: {
        'ui:placeholder': 'email@exemplo.com',
      },
      tipo_contrato: {
        'ui:placeholder': 'Selecione o tipo de contrato',
        'ui:emptyValue': '',
      },
      contrato_fornecedor: {
        'ui:widget': 'fornecedor-selector',
        'ui:placeholder': 'Selecione o fornecedor',
      },
      contrato_objetivo: {
        'ui:widget': 'textarea',
        'ui:placeholder': 'Descreva o objetivo do contrato...',
        'ui:options': {
          rows: 3,
        },
      },
      inicio_contrato: {
        'ui:widget': 'date',
        'ui:options': {
          min: '1970-01-01',
          max: new Date().toISOString().split('T')[0],
        },
      },
      termino_contrato: {
        'ui:widget': 'date',
      },
      empresa_lotacao: {
        'ui:placeholder': 'Selecione a empresa',
        'ui:emptyValue': '',
      },
      unidade: {
        'ui:widget': 'unidade-selector',
        'ui:placeholder': 'Selecione a unidade',
      },
      cargo: {
        'ui:widget': 'cargo-selector',
        'ui:placeholder': 'Selecione o cargo',
      },
      // email_institucional: {
      //   'ui:placeholder': 'nome@fieac.org.br',
      // },
      // afastado: {
      //   'ui:widget': 'radio',
      //   'ui:options': {
      //     inline: true,
      //   },
      // },
      // microsoft_a5: {
      //   'ui:widget': 'radio',
      //   'ui:options': {
      //     inline: true,
      //   },
      // },
      // observacoes: {
      //   'ui:widget': 'textarea',
      //   'ui:placeholder': 'Digite observações adicionais...',
      //   'ui:options': {
      //     rows: 4,
      //   },
      // },
      secao_dados_pessoais: {
        'ui:widget': 'hidden',
        'ui:options': {
          classNames: 'section-header',
        },
      },
      secao_dados_contratuais: {
        'ui:widget': 'hidden',
        'ui:options': {
          classNames: 'section-header',
        },
      },
      secao_dados_lotacao: {
        'ui:widget': 'hidden',
        'ui:options': {
          classNames: 'section-header',
        },
      },
    },
  }
}

/**
 * Instância padrão do schema.
 */
export const solicitacaoCriacaoAcessoInstitucionalSchema =
  getSolicitacaoCriacaoAcessoInstitucionalSchema()
